<template>
    <div class="extensions-dashboard">
        <Portal :to="`widget-header__${data.WidgetID}`">
            <div>
                <VcSelect
                    v-model="sortByModel"
                    :multiple="false"
                    :placeholder="String(t('general.sortBy'))"
                    :options="extensionsSortOptionsWithoutCalls"
                    :config="{
                        labelKey: 'label',
                        valueKey: 'value'
                    }"
                    class="w-fit min-w-[220px]"
                />
            </div>
        </Portal>
        <div
            v-if="sortedExtensionsDebounced.length"
            ref="parent"
            class="flex flex-wrap items-stretch gap-4 justify-center"
        >
            <div
                v-for="extension in sortedExtensionsDebounced"
                :key="`${data.WidgetID}-${extension.number}`"
                class="card-wrap"
            >
                <extensions-dashboard-card
                    :data="data"
                    :extension="extension"
                    :settings="settings"
                    @recently-logged-out-expired="markExpired(`${extension.userID}:${extension.number}`)"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import ExtensionsDashboardCard
    from '@/components/RedesignComponents/Cards/ExtensionsDashbordCards/ExtensionsDashboardCard.vue'
import { useExtensions } from '@/composables/useExtensions'
import { WidgetExtended } from '@/types/dashboard'
import useWidgetDataWithRefreshInterval from '@/composables/useWidgetDataWithRefreshInterval'
import { computed, ref, watch } from 'vue'
import { realTimeSettings } from '@/enum/defaultWidgetSettings'
import useEntityPositiveNegativeFilters from '@/composables/useEntityPositiveNegativeFilters'
import { EXTENSION_LIST_PARAMETER_NAME } from '@/enum/widgetTemplateConfigs'
import cloneDeep from 'lodash/cloneDeep'
import { getExtensionsSortOptionsWithoutCalls } from '@/enum/extensionsOptions'
import { useI18n } from 'vue-i18n'
import { VcSelect } from '@voicenter-team/voicenter-ui-plus'
import { getInitialExtensionTime } from '@/util/timeUtils'
import orderBy from 'lodash/orderBy'
import { useThrottle } from '@vueuse/core'
import { useAutoAnimate } from '@formkit/auto-animate/vue'
import { Extension } from '@/types/extension'
import { currentAccountEntitiesExtensions } from '@/composables/globalData/useEntitiesData'
import {
    isRecentlyLoggedOutEnabled,
    keepAfterExpiredKeys,
    shouldSkipExpiredLogout,
    useExpiredRecentlyLoggedOutKeys
} from '@/composables/useExpiredRecentlyLoggedOutKeys'

interface DataLoaded {
    user_id: number,
    exten_id: number
    [key: string]: string | number;
}
interface SortedExtension extends Extension {
    statusTime: number
}

const extensionIdProp = 'number'

const { t } = useI18n()

const props = defineProps<{
    data: WidgetExtended
}>()

const emit = defineEmits<{
    (event: 'on-update', widgetData: WidgetExtended): void
}>()

const localWidgetData = ref(cloneDeep(props.data))
const sortByModel = ref('number-asc')

const [ parent ] = useAutoAnimate({
    duration: 300
})

const { extensions, onlineExtensions } = useExtensions()
const { data: loadedData } = useWidgetDataWithRefreshInterval<DataLoaded[], {data: WidgetExtended}, 'data'>(props, 'data')


const settings = computed(() => {
    return localWidgetData.value?.WidgetLayout?.settings || realTimeSettings
})

// Get sort options with translated labels at runtime
const extensionsSortOptionsWithoutCalls = computed(() => getExtensionsSortOptionsWithoutCalls())

const showLoggedOutUsers = computed(() => {
    return props.data.WidgetLayout?.settings?.showLoggedOutUsers
})

const { expiredKeys, markExpired, clearExpired } = useExpiredRecentlyLoggedOutKeys(() => settings.value)

const currentExtensions = computed(() => {
    const sourceExtensions = showLoggedOutUsers.value
        ? extensions.value
        : onlineExtensions.value

    return sourceExtensions.filter((ext) => {
        if (!currentAccountEntitiesExtensions.value[ext.number]) return false

        if (shouldSkipExpiredLogout(ext, settings.value)) return false

        return true
    })
})

const { filteredItems: filteredExtensions } = useEntityPositiveNegativeFilters(localWidgetData, EXTENSION_LIST_PARAMETER_NAME, currentExtensions, extensionIdProp)

const usersData = computed(() => {
    if (!loadedData?.value?.length) {
        return null
    }
    return loadedData.value.reduce<Record<number, DataLoaded>>((accum, user) => {
        accum[user.exten_id] = user
        return accum
    }, {})
})

const mergedFilteredExtensions = computed(() => {
    if (usersData.value) {
        return filteredExtensions.value.map((item) => {
            const user = usersData.value ? usersData.value[item.number] : {}
            if (user) {
                return {
                    ...item,
                    allDataSummary: user
                }
            }
            return item
        })
    }
    return filteredExtensions.value
})

const sortedExtensions = computed(() => {
    if (!mergedFilteredExtensions.value.length) {
        return []
    }
    const updatedExtensions = mergedFilteredExtensions.value.map(ext => {
        const extension: SortedExtension = {
            ...ext,
            statusTime: getInitialExtensionTime(ext, settings.value)
        }
        return extension
    })
    const [ prop, order ] = sortByModel.value.split('-')
    return orderBy(updatedExtensions, prop, order)
})

const sortedExtensionsVisible = computed(() => {
    if (!isRecentlyLoggedOutEnabled(settings.value)) return sortedExtensions.value
    return sortedExtensions.value.filter((ext) =>
        keepAfterExpiredKeys(
            `${ext.userID}:${ext.number}`,
            ext.representativeStatus,
            expiredKeys.value,
            clearExpired
        )
    )
})

const sortedExtensionsDebounced = useThrottle(sortedExtensionsVisible, 500)

//Methods
const updateWidget = (widgetData: WidgetExtended) => {
    emit('on-update', widgetData)
}

//Watch
watch(() => props.data.WidgetLayout?.sortingParams, (sortingParams) => {
    const option = extensionsSortOptionsWithoutCalls.value.find(o => o.prop === sortingParams?.prop && o.order === sortingParams?.order)
    sortByModel.value = option?.value ?? 'number-asc'
}, { immediate: true })

watch(sortByModel, value => {
    const selectedOption = extensionsSortOptionsWithoutCalls.value.find(o => o.value === value)

    if (selectedOption) {
        localWidgetData.value.WidgetLayout.sortingParams = {
            prop: selectedOption.prop,
            order: selectedOption.order
        }
        updateWidget(localWidgetData.value)
    }
})
watch(
    () => props.data,
    (newV) => {
        localWidgetData.value = cloneDeep(newV)
    },
    {
        deep: true
    }
)
</script>

<style scoped lang="scss">
.extensions-dashboard {
  .card-wrap {
    width: 452px;
  }
}

</style>
